import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Container, Row } from "reactstrap";
import { isEmpty } from "../../../../utilities/typeChecking";
import { notifyWarning } from "../../../../actions";
import SearchMonitoringQueueEntriesForm from "../../components/searching/search-form";
import NoEntriesMessage from "../../../../components/common/NoEntriesMessage/NoEntriesMessage";
import {
  setSearchFilters,
  getMonitoringQueueEntries,
  getMonitoringQueueEntriesCount,
  setCurrentPage,
  clearMonitoringQueueEntries,
  getScoreCardRiskFactors,
  setItemsPerPage,
  setTableCheckboxChecked,
  setTableHeaderDropdownValue,
  setSelectedEntries,
  setDeselectedEntries,
} from "../../actions";
import {
  pageInfoSelector,
  selector,
  exportEntries,
  riskFactorsSelector,
  monitoringQueueIsLoadingSelector,
  selectTableCheckboxChecked,
  selectTableHeaderDropdownValue,
  selectSelectedEntries,
  selectDeselectedEntries,
  noEntriesMessageSelectorMonitoringQueue,
} from "../../selectors";
import MonitoringQueueEntriesTable from "../../components/searching/search-form/table";
import "./monitoringQueueEntriesPage.css";
import Pagination from "../../../../components/Pagination/Pagination";
import { PageTitle } from "../../../../components/common";
import FullscreenLoader from "../../../../layouts/FullScreenLoader";
import { organizationInfoSelector } from "../../../organization/selectors";
import { trimFormData } from "../../../../utilities/filters";
import { TABLE_HEADER_DROPDOWN_VALUES } from "../../constants/monitoringQueueEntryConstants";
import { mainClientTypeSelector } from "../../../../selectors/getScoreCardByTypeSelectors";

const MonitoringQueueEntriesPage = ({ location }) => {
  const monitoringQueueEntries = useSelector(selector);
  const csvFile = useSelector(exportEntries);
  const riskFactors = useSelector(riskFactorsSelector);
  const clientTypes = useSelector(mainClientTypeSelector);
  const isLoading = useSelector(monitoringQueueIsLoadingSelector);
  const organizationInfo = useSelector(organizationInfoSelector);
  const tableCheckboxChecked = useSelector(selectTableCheckboxChecked);
  const tableHeaderDropdownValue = useSelector(selectTableHeaderDropdownValue);
  const isFirstRender = useRef(true);
  const { count, itemsPerPage, currentPage } = useSelector(pageInfoSelector);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const selectedEntries = useSelector(selectSelectedEntries);
  const deselectedEntries = useSelector(selectDeselectedEntries);
  const noEntriesMessage = useSelector(noEntriesMessageSelectorMonitoringQueue);

  const pageHaveSelectedEntry = useMemo(() => {
    return monitoringQueueEntries.some((e) => selectedEntries.includes(e.id));
  }, [selectedEntries, currentPage, monitoringQueueEntries]);

  const dispatch = useDispatch();

  const [isOpenCollapse, setIsOpenCollapse] = useState(true);

  const toggleCollapse = () => setIsOpenCollapse(!isOpenCollapse);

  const handleTableCheckboxChecked = (bool) => {
    dispatch(setTableCheckboxChecked(bool));
  };

  const handleTableHeaderDropdownValueChange = (value) => {
    dispatch(setTableHeaderDropdownValue(value));
  };

  const handleSetSelectedEntries = (entries) => {
    dispatch(setSelectedEntries(entries));
  };

  const handleSetDeselectedEntries = (entries) => {
    dispatch(setDeselectedEntries(entries));
  };

  useEffect(
    () => () => {
      dispatch(clearMonitoringQueueEntries());
    },
    []
  );

  useEffect(() => {
    if (count > 0 && isSubmitted) {
      dispatch(getMonitoringQueueEntries({ take: itemsPerPage }));
      setIsSubmitted(false);
    }
  }, [count]);

  useEffect(() => {
    if (riskFactors.length === 0) {
      dispatch(getScoreCardRiskFactors());
    }
  }, [riskFactors]);

  useEffect(() => {
    switch (tableHeaderDropdownValue) {
      case TABLE_HEADER_DROPDOWN_VALUES.ALL:
        if (tableCheckboxChecked) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      case TABLE_HEADER_DROPDOWN_VALUES.ON_THIS_PAGE:
        if (selectedEntries.length && pageHaveSelectedEntry) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      case TABLE_HEADER_DROPDOWN_VALUES.NONE:
        if (selectedEntries.length && pageHaveSelectedEntry) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      default:
        break;
    }
  }, [monitoringQueueEntries, selectedEntries]);

  useEffect(() => {
    switch (tableHeaderDropdownValue) {
      case TABLE_HEADER_DROPDOWN_VALUES.ALL:
        if (tableCheckboxChecked) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      case TABLE_HEADER_DROPDOWN_VALUES.ON_THIS_PAGE:
        if (selectedEntries.length && pageHaveSelectedEntry) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      case TABLE_HEADER_DROPDOWN_VALUES.NONE:
        if (selectedEntries.length && pageHaveSelectedEntry) {
          handleTableCheckboxChecked(true);
        } else {
          handleTableCheckboxChecked(false);
        }
        break;
      default:
        break;
    }
  }, [monitoringQueueEntries, selectedEntries]);

  useEffect(() => {
    if (csvFile.fileBytes) {
      if (!isEmpty(csvFile) && csvFile.fileBytes.size <= 3) {
        dispatch(notifyWarning("There are no records for export yet"));
      } else if (!isEmpty(csvFile) && csvFile.fileBytes.size > 3) {
        // IE doesn't allow using a blob object directly as link href
        // instead it is necessary to use msSaveOrOpenBlob

        if (window.navigator && window.navigator.msSaveOrOpenBlob) {
          // window.navigator.msSaveOrOpenBlob(url);
          window.navigator.msSaveOrOpenBlob(
            csvFile.fileBytes,
            csvFile.fileName
          );
          return;
        }

        const url = URL.createObjectURL(csvFile.fileBytes);
        const link = document.createElement("a");
        link.href = url;
        link.download = csvFile.fileName;
        link.click();
        setTimeout(() => {
          // For Firefox it is necessary to delay revoking the ObjectURL
          window.URL.revokeObjectURL(url);
        }, 100);
      }
    }
  }, [csvFile]);

  const handleSubmit = (data, e) => {
    if (!isOpenCollapse) {
      return;
    }
    const trimmedData = trimFormData(data);
    dispatch(setSearchFilters(trimmedData));
    handleSetSelectedEntries([]);
    handleSetDeselectedEntries([]);
    handleTableCheckboxChecked(false);
    handleTableHeaderDropdownValueChange(
      TABLE_HEADER_DROPDOWN_VALUES.ON_THIS_PAGE
    );
    dispatch(getMonitoringQueueEntriesCount(trimmedData));
    setIsSubmitted(true);
    setIsOpenCollapse(false);
  };

  const selectEntry = (data) => {
    const checked = data.target.checked;
    const id = parseInt(data.target.id, 10);

    if (checked) {
      selectedEntries.push(id);
      handleSetDeselectedEntries([
        ...deselectedEntries.splice(deselectedEntries.indexOf(id), 1),
      ]);
    } else if (!checked) {
      handleSetDeselectedEntries([...deselectedEntries.push(id)]);
      selectedEntries.splice(selectedEntries.indexOf(id), 1);
    }
    handleSetSelectedEntries(selectedEntries);
    handleSetDeselectedEntries([...deselectedEntries]);
  };

  const onPageChange = (_, page) => {
    dispatch(setCurrentPage(page + 1));
    const skip = page * itemsPerPage;
    dispatch(getMonitoringQueueEntries({ take: itemsPerPage, skip }));
  };

  const onRowsPerPageChange = ({ target }) => {
    dispatch(setCurrentPage(1));
    dispatch(setItemsPerPage(target.value));
  };

  useEffect(() => {
    if (isFirstRender.current || parseInt(count, 10) === 0) {
      isFirstRender.current = false;
      return;
    } else {
      dispatch(setCurrentPage(1));
      const skip = (currentPage - 1) * itemsPerPage;
      dispatch(getMonitoringQueueEntries({ take: itemsPerPage, skip }));
    }
  }, [itemsPerPage]);

  function dropdownitemchange(data) {
    var value = data.target.value;
    if (value === TABLE_HEADER_DROPDOWN_VALUES.NONE) {
      handleSetDeselectedEntries([]);
      handleSetSelectedEntries([]);
      handleTableCheckboxChecked(false);
      handleTableHeaderDropdownValueChange(value);
      return;
    }
    checkBoxChangeSelection(null, true);
    handleTableHeaderDropdownValueChange(value);
    if (value === TABLE_HEADER_DROPDOWN_VALUES.ALL) {
      const selectedEntries = [];
      monitoringQueueEntries.forEach((a) => {
        var id = parseInt(a.id, 10);
        if (!selectedEntries.includes(id)) {
          selectedEntries.push(id);
        }
      });
      handleSetSelectedEntries(selectedEntries);
    }
  }

  function checkBoxChangeSelection(selected, manual) {
    const checked = manual ? manual : !tableCheckboxChecked;
    handleTableCheckboxChecked(checked);
    if (
      tableHeaderDropdownValue === TABLE_HEADER_DROPDOWN_VALUES.ALL &&
      checked === false
    ) {
      handleSetSelectedEntries([]);
      handleSetDeselectedEntries([]);
      return;
    }
    monitoringQueueEntries.forEach((a) => {
      const id = parseInt(a.id, 10);
      if (checked && !selectedEntries.includes(id)) {
        selectedEntries.push(id);
        deselectedEntries.splice(deselectedEntries.indexOf(id), 1);
      } else if (!checked && selectedEntries.includes(id)) {
        deselectedEntries.push(id);
        selectedEntries.splice(selectedEntries.indexOf(id), 1);
      }
    });
    handleSetSelectedEntries(selectedEntries);
    handleSetDeselectedEntries(deselectedEntries);
  }

  return (
    <Container
      className="monitoring-queue-entries-page-container-main col-12"
      tabIndex="0"
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          document
            .getElementById("searchMonitoringQueueEntriesSubmitButton")
            .click();
        }
      }}
    >
      <PageTitle title="monitoring queue" />
      <FullscreenLoader loading={isLoading} />

      {count ? null : (
        <div className="monitoring-queue-entries-page-background-logo" />
      )}
      <SearchMonitoringQueueEntriesForm
        isOpenCollapse={isOpenCollapse}
        toggleCollapse={toggleCollapse}
        handleSubmit={handleSubmit}
        location={location}
        riskFactors={riskFactors}
        clientTypes={clientTypes}
        monitoringQueueEntries={monitoringQueueEntries}
        allSelected={
          tableCheckboxChecked &&
          tableHeaderDropdownValue === TABLE_HEADER_DROPDOWN_VALUES.ALL
        }
        organizationInfo={organizationInfo}
      />

      {monitoringQueueEntries.length > 0 ? (
        <div style={{ position: "relative" }}>
          <MonitoringQueueEntriesTable
            monitoringQueueEntries={monitoringQueueEntries}
            selectEntry={selectEntry}
            selectedEntries={selectedEntries}
            checkBoxChangeSelection={checkBoxChangeSelection}
            dropdownitemchange={dropdownitemchange}
            organizationInfo={organizationInfo}
            deselectedEntries={deselectedEntries}
          />
          {isOpenCollapse ? (
            <div className="overlay-collapse-open" onClick={toggleCollapse} />
          ) : null}

          {/* {true ? ( // this can be a loader for the table [currently no loader in design]
            <div
              className="overlay-collapse-open overlay-collapse-open-loading"
              onClick={toggleCollapse}
            >
              <CircularProgress size={60} color="inherit" />
            </div>
          ) : null} */}

          {count ? (
            <Row className="pagination-row mt-5 mb-5">
              <Pagination
                count={count}
                onPageChange={onPageChange}
                onRowsPerPageChange={onRowsPerPageChange}
                rowsPerPage={itemsPerPage}
                page={currentPage - 1}
              />
            </Row>
          ) : null}
        </div>
      ) : (
        <NoEntriesMessage payload={noEntriesMessage} />
      )}
    </Container>
  );
};

export default MonitoringQueueEntriesPage;



// WEBPACK FOOTER //
// ./src/features/monitoring-queue-entry/pages/monitoring-queue-entries-page/MonitoringQueueEntriesPage.jsx